import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Clock,
  MapPin,
  RefreshCw,
  User,
  Navigation,
  Building2,
  CheckCircle2,
  Loader2,
  Lock,
  CalendarClock,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useTasks } from '../../context/TasksContext';
import { useCompany } from '../../context/CompanyContext';
import { toast } from 'sonner';
import { getDisplayName } from '../../lib/utils';
import RescheduleJobModal from '../../components/scheduler/RescheduleJobModal';
import WorkOrderSheet from '../../features/clients/components/profile/WorkOrderSheet';
import ConfirmCompleteDialog from '../../components/jobs/ConfirmCompleteDialog';
import { resolveDisplayedStep } from '../../utils/resolveDisplayedStep';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Status configuration ───────────────────────────────────────────────────────

const STATUS_CONFIG = {
  approved:  { label: 'Ready',     badgeClass: 'bg-slate-100 text-slate-600',    dot: 'bg-slate-400' },
  scheduled: { label: 'Scheduled', badgeClass: 'bg-blue-100 text-blue-700',      dot: 'bg-blue-400'  },
  en_route:  { label: 'En Route',  badgeClass: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-500' },
  on_site:   { label: 'On Site',   badgeClass: 'bg-indigo-100 text-indigo-700',  dot: 'bg-indigo-500' },
  completed: { label: 'Complete',  badgeClass: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

const NEXT_ACTION = {
  approved:  { label: 'En Route',  nextStatus: 'en_route', btnClass: 'bg-amber-500 hover:bg-amber-600 text-white' },
  scheduled: { label: 'En Route',  nextStatus: 'en_route', btnClass: 'bg-amber-500 hover:bg-amber-600 text-white' },
  en_route:  { label: 'On Site',   nextStatus: 'on_site',  btnClass: 'bg-indigo-500 hover:bg-indigo-600 text-white' },
  on_site:   { label: 'Complete',  nextStatus: 'completed', btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white', isComplete: true },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTime(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function getAddress(lead) {
  if (!lead) return null;
  if (lead.addressDisplay) return lead.addressDisplay;
  if (lead.address) {
    if (typeof lead.address === 'string') return lead.address;
    const { line1, city, state } = lead.address;
    return [line1, city, state].filter(Boolean).join(', ');
  }
  if (lead.clientInfo?.city) return lead.clientInfo.city;
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

const DispatchJobCard = ({ task, lead, canChangeStatus, canDrag, isDragOverlay, isSelected, onToggleSelect }) => {
  const { updateTask, completeJob } = useTasks();
  const { companyId } = useCompany();
  const [acting, setActing] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAppointment = (task.jobMode || 'route') === 'appointment';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    disabled: !canDrag || task.status === 'completed' || isAppointment,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : undefined,
  };

  const status = task.status || 'approved';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.approved;
  const nextAction = NEXT_ACTION[status];

  const startTime = formatTime(task.scheduledStart);
  const endTime   = formatTime(task.scheduledEnd);
  const address   = getAddress(lead);
  const clientName = task.clientName || (lead ? getDisplayName(lead, '') : '') || 'Unknown Client';
  const serviceType = task.serviceType || task.title || lead?.service || 'Service';

  // ── En-route SMS with ETA (non-blocking) ─────────────────────────────────────
  const sendEnRouteSms = async () => {
    try {
      const cid = companyId || task.companyId;
      if (!cid) return;
      const token = await getAuth().currentUser?.getIdToken();
      let techLat = null;
      let techLng = null;

      // Request geolocation (5-second timeout, non-fatal if denied)
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        techLat = pos.coords.latitude;
        techLng = pos.coords.longitude;
      } catch {
        // Geolocation denied or unavailable — ETA will fall back to "shortly"
      }

      await axios.post(
        `${API}/api/notifications/visit-reminders/en-route`,
        { companyId: cid, taskId: task.id, techLat, techLng },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Customer notified — on the way!');
    } catch (err) {
      // Non-blocking: don't surface to user unless debugging
      console.warn('[DispatchJobCard] En-route SMS failed (non-blocking):', err?.response?.data || err.message);
    }
  };

  const handleStatusAction = async () => {
    if (!nextAction || !canChangeStatus || acting) return;

    if (nextAction.isComplete) {
      setConfirmOpen(true); // completion safeguard — confirm before billing fires
      return;
    }

    setActing(true);
    try {
      await updateTask(task.id, { status: nextAction.nextStatus });
      // En-route transition → fire SMS with ETA in background
      if (nextAction.nextStatus === 'en_route') {
        sendEnRouteSms(); // fire-and-forget
      }
    } catch (err) {
      console.error('[DispatchJobCard] status update failed:', err);
      toast.error('Failed to update status');
    } finally {
      setActing(false);
    }
  };

  // Canonical completion — always via completeJob() (billing + recurrence)
  const performComplete = async () => {
    setActing(true);
    try {
      const data = await completeJob(task.id, {
        quoteId: task.quoteId,
        originUrl: window.location.origin,
      });
      // Smart toast: reflect the actual billing outcome
      if (data?.billingWarning) {
        toast.warning(
          data.message || 'Job complete — invoice could not be created',
          {
            description: data.billingWarning === 'stripe_not_configured'
              ? 'Configure Stripe in Settings → Billing.'
              : 'Open Invoices to retry — the failed entry is saved there.',
            action: { label: 'Open Invoices', onClick: () => { window.location.href = '/invoices'; } },
            duration: 12000,
          }
        );
      } else if (data?.autoCharged) {
        const amt = data.autoChargeAmount != null
          ? `$${parseFloat(data.autoChargeAmount).toFixed(2)}`
          : '';
        const card = data.cardBrand && data.cardLast4
          ? ` via ${data.cardBrand} ••••${data.cardLast4}`
          : ' via card on file';
        toast.success(`Job complete — auto-charged ${amt}${card}`);
      } else if (data?.autoChargeFailed) {
        toast.warning('Job complete — auto-charge failed. Payment link sent.');
      } else if (data?.isCoveredByPackage) {
        toast.success('Job complete — covered by prepay package');
      } else if (data?.smsSent) {
        toast.success('Job complete — payment link sent to customer');
      } else {
        toast.success('Job marked complete');
      }
    } catch (err) {
      console.error('[DispatchJobCard] complete failed:', err);
      toast.error('Failed to update status');
    } finally {
      setActing(false);
    }
  };

  // Undo On Site — revert an accidental arrival back to En Route
  const handleUndoOnSite = async () => {
    if (acting || status !== 'on_site' || !canChangeStatus) return;
    setActing(true);
    try {
      await updateTask(task.id, { status: 'en_route', arrivedAt: null });
      toast.success('Reverted to En Route');
    } catch (err) {
      console.error('[DispatchJobCard] undo on-site failed:', err);
      toast.error('Failed to revert status');
    } finally {
      setActing(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={isDragOverlay ? {} : style}
      data-testid={`dispatch-job-card-${task.id}`}
      onClick={() => { if (!isDragOverlay) setDetailOpen(true); }}
      className={`
        relative bg-white rounded-lg border shadow-sm cursor-pointer
        ${status === 'completed'
          ? 'border-emerald-200 opacity-70'
          : isSelected
            ? 'border-emerald-500 ring-2 ring-emerald-200 shadow-md'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
        }
        ${isDragOverlay ? 'shadow-xl rotate-1 border-[var(--cb-orange)]' : ''}
        transition-all duration-150
      `}
    >
      {/* Card header row */}
      <div className="flex items-start gap-2 px-3 pt-3 pb-1">
        {/* Multi-select checkbox — only when parent enables selection.
            Hidden in drag overlay and for completed jobs (no point moving). */}
        {onToggleSelect && !isDragOverlay && task.status !== 'completed' && task.scheduledStart && (
          <label
            className="flex-shrink-0 inline-flex items-center mt-0.5 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            title={isSelected ? 'Remove from bulk selection' : 'Select for bulk move'}
          >
            <input
              type="checkbox"
              checked={!!isSelected}
              onChange={() => onToggleSelect(task.id)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              data-testid={`dispatch-select-checkbox-${task.id}`}
            />
          </label>
        )}
        {/* Drag handle (route) or fixed indicator (appointment) */}
        {canDrag && status !== 'completed' ? (
          <div
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
            data-testid={`dispatch-drag-handle-${task.id}`}
          >
            <GripVertical className="h-4 w-4" />
          </div>
        ) : isAppointment ? (
          <div
            className="mt-0.5 text-gray-200 flex-shrink-0"
            title="Fixed appointment — time is the constraint"
            data-testid={`dispatch-appt-lock-${task.id}`}
          >
            <Lock className="h-4 w-4" />
          </div>
        ) : null}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Client name */}
          <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
            {clientName}
          </p>
          {/* Service type */}
          <p className="text-xs text-gray-500 truncate mt-0.5">{serviceType}</p>
          {/* Fixed appointment badge */}
          {isAppointment && (
            <span className="inline-flex items-center gap-0.5 mt-0.5 px-1.5 py-0.5 text-[10px] font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-full" data-testid={`dispatch-appt-badge-${task.id}`}>
              <Lock className="h-2.5 w-2.5" />
              Fixed Appt
            </span>
          )}
        </div>

        {/* Status badge */}
        <span
          data-testid={`dispatch-status-badge-${task.id}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${statusCfg.badgeClass}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
          {statusCfg.label}
        </span>
      </div>

      {/* Card body */}
      <div className="px-3 pb-2 space-y-1">
        {/* Time window */}
        {startTime && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock className="h-3 w-3 flex-shrink-0" />
            <span>{startTime}{endTime ? ` – ${endTime}` : ''}</span>
          </div>
        )}

        {/* Address */}
        {address && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500">
            <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
            <span className="truncate">{address}</span>
          </div>
        )}

        {/* Assigned tech (shown in unassigned rail only — where no tech column is present) */}
        {task.assignedToName && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <User className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{task.assignedToName}</span>
          </div>
        )}

        {/* Recurring indicator OR visit sequence badge */}
        {task.isRecurring && (
          task.servicePlanStep || task.visitNumber || task.seriesIndex ? (
            (() => {
              const { displayed: dstep, hasOverride } = resolveDisplayedStep(task);
              return (
                <div
                  className="mt-1.5 rounded-md bg-emerald-50 border border-emerald-100 px-2.5 py-2 space-y-1"
                  data-testid={`dispatch-visit-badge-${task.id}`}
                >
                  {/* Row 1 — Visit counter */}
                  <div className="flex items-center gap-1.5">
                    <RefreshCw className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs font-semibold text-emerald-700">
                      Visit {task.visitNumber || task.seriesIndex}
                      {lead?.recurrence?.totalOccurrences
                        ? ` of ${lead.recurrence.totalOccurrences}`
                        : ''}
                    </span>
                    {hasOverride && (
                      <span
                        className="ml-1 text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-0.5"
                        data-testid={`dispatch-visit-customized-chip-${task.id}`}
                      >
                        Customized
                      </span>
                    )}
                  </div>
                  {/* Row 2 — Program name */}
                  {task.programName && (
                    <p className="text-[11px] font-medium text-emerald-600 truncate pl-4">
                      {task.programName}
                    </p>
                  )}
                  {/* Row 3 — Step label */}
                  {dstep.label && (
                    <p className="text-[11px] text-emerald-600 truncate pl-4">
                      {dstep.label}
                    </p>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-emerald-600 mt-1">
              <RefreshCw className="h-3 w-3 flex-shrink-0" />
              <span className="font-medium">Recurring</span>
            </div>
          )
        )}
      </div>

      {/* Status action button */}
      {nextAction && canChangeStatus && status !== 'completed' && (
        <div className="px-3 pb-3">
          <button
            type="button"
            data-testid={`dispatch-action-btn-${task.id}`}
            onClick={(e) => { e.stopPropagation(); handleStatusAction(); }}
            disabled={acting}
            className={`
              w-full flex items-center justify-center gap-1.5
              text-xs font-medium py-1.5 px-3 rounded-md
              transition-colors duration-150
              disabled:opacity-60 disabled:cursor-not-allowed
              ${nextAction.btnClass}
            `}
          >
            {acting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : nextAction.isComplete ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : (
              <Navigation className="h-3 w-3" />
            )}
            {acting ? 'Updating…' : nextAction.label}
          </button>
          {/* Undo — revert accidental On Site back to En Route (canonical scope: on_site only) */}
          {status === 'on_site' && (
            <button
              type="button"
              data-testid={`dispatch-undo-btn-${task.id}`}
              onClick={(e) => { e.stopPropagation(); handleUndoOnSite(); }}
              disabled={acting}
              title="Revert to En Route — use this if arrival was logged by mistake"
              className="w-full mt-1 text-[11px] font-medium py-1 px-3 rounded-md text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60"
            >
              Undo — back to En Route
            </button>
          )}
        </div>
      )}

      {/* Completed indicator */}
      {status === 'completed' && (
        <div className="px-3 pb-3">
          <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-600 font-medium py-1.5 bg-emerald-50 rounded-md">
            <CheckCircle2 className="h-3 w-3" />
            Completed
          </div>
        </div>
      )}

      {/* Reschedule button (hidden on completed) — opens the two-mode RescheduleJobModal */}
      {status !== 'completed' && canChangeStatus && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setRescheduleOpen(true); }}
          className="absolute top-2 right-2 p-1 rounded-md text-gray-300 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          title="Reschedule"
          data-testid={`dispatch-reschedule-btn-${task.id}`}
        >
          <CalendarClock className="h-3.5 w-3.5" />
        </button>
      )}

      <RescheduleJobModal
        task={task}
        isOpen={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
      />

      {/* Canonical Work Order detail — same component as Customer Profile / Today */}
      {detailOpen && (
        <WorkOrderSheet
          task={task}
          client={lead}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          onCompleted={() => setDetailOpen(false)}
        />
      )}

      {/* Completion safeguard */}
      <ConfirmCompleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        clientName={clientName}
        taskId={task?.id}
        onConfirm={() => { setConfirmOpen(false); performComplete(); }}
      />
    </div>
  );
};

export default DispatchJobCard;
